import { RplDTO } from '../../services/apiClient';
import { TableAction } from '../common/Table';
import {
  Check,
  Clock,
  DollarSign,
  Download,
  FileText,
  Mail,
  MessageSquareText,
  RotateCcw,
  Search,
  Star,
  User,
  Trash2,
  UserCog,
} from 'lucide-react';
import { RplStatusConstants } from '../../constants/RplStatusConstants';

interface RplActionHandlers {
  onViewMemberDetails: (rpl: RplDTO) => void;
  onView: (rpl: RplDTO) => void;
  onApprove: (rpl: RplDTO) => void;
  onChangeStatus: (rpl: RplDTO) => void;
  onDelete: (rpl: RplDTO) => Promise<void>;
  onSendEmail: (member: RplDTO) => void;
  onViewAllEmails: (member: RplDTO) => void;
  onViewRecordEmails: (member: RplDTO) => void;
  onRestore: (rpl: RplDTO) => Promise<void>;
  onImpersonateMemberLogin: (member: RplDTO) => void;
  onAddNote: (rpl: RplDTO) => void;
  onViewNoteHistory: (rpl: RplDTO) => void;
  onViewInvoiceHistory: (member: RplDTO) => void;
  onSendProofOfPaymentLink: (rpl: RplDTO) => void;
  onCompleteEftPayment: (rpl: RplDTO) => void;
  onDownloadForfeitureDocument?: (rpl: RplDTO) => void;
  onDownloadOriginalCertificate?: (rpl: RplDTO) => void;
}

const createRplActions =
  (handlers: RplActionHandlers) =>
    (row: RplDTO): TableAction[] => {
      const actions: TableAction[] = [];
      const isForfeited = row.rplStatusId?.toLowerCase() === RplStatusConstants.FORFEITED;

      if (!row.isDeleted && !isForfeited) {
        actions.push({
          label: 'View Member',
          onClick: () => handlers.onViewMemberDetails(row),
          icon: <User className="w-4 h-4" />,
        });
      }
      if (!row.isDeleted && !isForfeited && handlers.onView) {
        actions.push({
          label: 'View Record',
          onClick: () => handlers.onView(row),
          icon: <Search className="w-4 h-4" />,
        });
        actions.push({
          label: 'Login as Member',
          onClick: () => handlers.onImpersonateMemberLogin(row),
          icon: <UserCog className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to login as this member?',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onSendEmail) {
        actions.push({
          label: 'Send Email',
          onClick: () => handlers.onSendEmail(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onViewAllEmails) {
        actions.push({
          label: 'Messages(All)',
          onClick: () => handlers.onViewAllEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onViewRecordEmails) {
        actions.push({
          label: 'Messages(Record)',
          onClick: () => handlers.onViewRecordEmails(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onSendProofOfPaymentLink) {
        actions.push({
          label: 'Email Proof Upload?',
          onClick: () => handlers.onSendProofOfPaymentLink(row),
          icon: <Mail className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to send the proof of payment link to this member?',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onAddNote) {
        actions.push({
          label: 'Add Note',
          onClick: () => handlers.onAddNote(row),
          icon: <MessageSquareText className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && handlers.onViewNoteHistory) {
        actions.push({
          label: 'Notes History',
          onClick: () => handlers.onViewNoteHistory(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }


      if (!row.isDeleted && !isForfeited && handlers.onCompleteEftPayment) {
        actions.push({
          label: 'Paid',
          onClick: () => handlers.onCompleteEftPayment(row),
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600 hover:text-green-700',
          confirmMessage: 'Are you sure you want to mark this RPL as paid?',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onApprove) {
        actions.push({
          label: 'Approve',
          onClick: () => handlers.onApprove(row),
          icon: <Check className="w-4 h-4" />,
          className: 'text-green-600 hover:text-green-700',
        });
      }

      if (!row.isDeleted && !isForfeited && handlers.onChangeStatus) {
        actions.push({
          label: 'Change Status',
          onClick: () => handlers.onChangeStatus(row),
          icon: <Star className="w-4 h-4" />,
          className: 'text-orange-600 hover:text-orange-700',
        });
      }


      if (!row.isDeleted && !isForfeited && handlers.onViewInvoiceHistory) {
        actions.push({
          label: 'Invoice History',
          onClick: () => handlers.onViewInvoiceHistory(row),
          icon: <Clock className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && isForfeited && handlers.onDownloadOriginalCertificate) {
        actions.push({
          label: 'Download Original Certificate',
          onClick: () => handlers.onDownloadOriginalCertificate!(row),
          icon: <Download className="w-4 h-4" />,
        });
      }

      if (!row.isDeleted && isForfeited && handlers.onDownloadForfeitureDocument) {
        actions.push({
          label: 'Download Forfeiture Document',
          onClick: () => handlers.onDownloadForfeitureDocument!(row),
          icon: <FileText className="w-4 h-4" />,
        });
      }

      if (row.isDeleted) {
        actions.push({
          label: 'Restore Deleted',
          onClick: () => handlers.onRestore(row),
          icon: <RotateCcw className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to restore this rpl?',
          className: 'text-green-600 hover:text-green-700',
        });
      } else if (!isForfeited) {
        actions.push({
          label: 'Delete Record',
          onClick: () => handlers.onDelete(row),
          icon: <Trash2 className="w-4 h-4" />,
          confirmMessage: 'Are you sure you want to delete this rpl?',
          className: 'text-red-600 hover:text-red-700',
        });
      }

      return actions;
    };

export default createRplActions;
